(() => {
  'use strict';

  const VERSION = 'header-round4-v1';
  const HEADER_SECTION_SELECTOR = '.section-header';
  const DRAWER_SELECTOR = 'header-drawer';
  const HOME_STATUS_HIDDEN_CLASS = 'cap-home-fitment__status--initial-hidden';

  const YARDS = {
    'fort worth': {
      query: '3300 Van Horn Avenue, Fort Worth, TX 76111',
      label: 'Open Fort Worth location in Maps'
    },
    mesquite: {
      query: '124 Lakeside Road, Mesquite, TX 75181',
      label: 'Open Mesquite location in Maps'
    }
  };

  const clean = (value) => String(value == null ? '' : value).replace(/\s+/g, ' ').trim();

  function shopRoot() {
    const root = clean(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function isStoreHome() {
    const rootPath = new URL(shopRoot(), window.location.origin).pathname.replace(/\/+$/, '') || '/';
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    return currentPath === rootPath;
  }

  function mapsUrl(query) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }

  function wrapAddressInMapLink(address, yard) {
    if (!address || !yard || address.closest('[data-cap-map-link]')) return;

    const link = document.createElement('a');
    link.className = 'cap-header-map-link';
    link.dataset.capMapLink = 'true';
    link.href = mapsUrl(yard.query);
    link.target = '_blank';
    link.rel = 'noopener';
    link.setAttribute('aria-label', yard.label);

    address.replaceWith(link);
    link.appendChild(address);
  }

  function enhanceLocationLinks(section) {
    section.querySelectorAll('.cap-header-location-address').forEach((block) => {
      const city = clean(block.querySelector('strong')?.textContent).toLowerCase();
      wrapAddressInMapLink(block.querySelector('address'), YARDS[city]);
    });

    section.querySelectorAll('[data-cap-header-location]').forEach((details) => {
      const city = clean(details.querySelector('.cap-header-location__toggle')?.textContent).toLowerCase();
      wrapAddressInMapLink(details.querySelector('.cap-header-location__panel address'), YARDS[city]);
    });
  }

  function accountMenuItem(drawer) {
    const source = drawer.querySelector('.menu-drawer__account');
    if (!source) return null;

    const item = document.createElement('a');
    item.className = 'cap-mobile-menu__item cap-mobile-menu__item--account';
    item.href = source.getAttribute('href') || '#';
    item.innerHTML = source.innerHTML;
    item.setAttribute('aria-label', clean(source.textContent) || 'Account');
    return item;
  }

  function availabilityMenuItem() {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'cap-mobile-menu__item cap-mobile-menu__item--availability';
    item.dataset.availabilityInfo = 'true';
    item.dataset.capMobileAvailability = 'true';
    item.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9"></circle>
        <path d="M12 10.5v6"></path>
        <path d="M12 7.5h.01"></path>
      </svg>
      <span>Part Availability Policy</span>
    `;
    return item;
  }

  function customizeMobileDrawer(section) {
    const drawer = section.querySelector(DRAWER_SELECTOR);
    const navigation = drawer?.querySelector('.menu-drawer__navigation');
    if (!drawer || !navigation) return;
    if (drawer.dataset.capMobileMenuReady === 'true') return;

    const list = document.createElement('ul');
    list.className = 'cap-mobile-menu';
    list.dataset.capMobileMenu = VERSION;
    list.setAttribute('role', 'list');

    const account = accountMenuItem(drawer);
    if (account) {
      const row = document.createElement('li');
      row.appendChild(account);
      list.appendChild(row);
    }

    const availabilityRow = document.createElement('li');
    availabilityRow.appendChild(availabilityMenuItem());
    list.appendChild(availabilityRow);

    navigation.replaceChildren(list);
    drawer.dataset.capMobileMenuReady = 'true';
  }

  function closeDrawerBeforeAvailability(trigger) {
    const details = trigger.closest('#Details-menu-drawer-container');
    if (!details?.open) return;
    const summary = details.querySelector(':scope > summary');
    if (summary instanceof HTMLElement) summary.click();
  }

  function markFindPartsPage() {
    document.documentElement.classList.toggle('cap-find-parts-page', Boolean(document.getElementById('cap-find-parts-app')));
  }

  function updateHomeInitialStatus() {
    const root = document.getElementById('cap-home-fitment-search');
    const status = root?.querySelector('.cap-home-fitment__status');
    if (!status) return;

    const isMobile = window.matchMedia('(max-width: 620px)').matches;
    const isInitialPrompt = clean(status.textContent).toLowerCase() === 'start by selecting a year.';
    status.classList.toggle(HOME_STATUS_HIDDEN_CLASS, isStoreHome() && isMobile && isInitialPrompt);
  }

  function observeHomeStatus() {
    const root = document.getElementById('cap-home-fitment-search');
    const status = root?.querySelector('.cap-home-fitment__status');
    if (!status) return;

    updateHomeInitialStatus();
    if (status.dataset.capInitialStatusObserver === VERSION) return;
    status.dataset.capInitialStatusObserver = VERSION;

    new MutationObserver(updateHomeInitialStatus).observe(status, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function initialize() {
    const section = document.querySelector(HEADER_SECTION_SELECTOR);
    if (!section) return false;

    enhanceLocationLinks(section);
    customizeMobileDrawer(section);
    markFindPartsPage();
    observeHomeStatus();
    return true;
  }

  function initializeWhenReady() {
    if (initialize()) return;
    let attempts = 0;
    const retry = () => {
      attempts += 1;
      if (initialize() || attempts >= 180) return;
      window.requestAnimationFrame(retry);
    };
    window.requestAnimationFrame(retry);
  }

  if (!document.documentElement.dataset.capHeaderRound4Events) {
    document.documentElement.dataset.capHeaderRound4Events = VERSION;

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const availability = target?.closest('[data-cap-mobile-availability]');
      if (availability) closeDrawerBeforeAvailability(availability);
    }, true);

    window.addEventListener('resize', updateHomeInitialStatus, { passive: true });
  }

  document.addEventListener('shopify:section:load', () => {
    window.requestAnimationFrame(initializeWhenReady);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWhenReady, { once: true });
  } else {
    initializeWhenReady();
  }
})();
